import { Button, ButtonTypes } from '@common/components';
import { AllergyAndPregnancyRiskInfo } from '@common/components/AllergyAndPregnancyRiskInfo';
import { CellWithChanges } from '@common/components/CellWithChanges/CellWithChanges';
import { StyledLink } from '@common/components/HyperlinkFileInput/styles';
import IconWithTooltip from '@common/components/IconWithTooltip/IconWithTooltip';
import { canUpdateProductDetailsInTask } from '@common/features/document/helpers';
import {
  ChangeType,
  DiffDto,
  IAssignmentChangesDto,
  IDocumentDto,
  IProductChangesDto,
  IProductDto,
  ProductStatus as ProductStatusEnum,
} from '@common/features/submission/types';
import { formatDateTimeNoSeconds } from '@common/helpers';
import { SubmissionTaskStatuses } from '@common/services/types';
import { ActionType, Dictionary, FileData, SubmissionTaskType } from '@common/types';
import { getLegalCategories } from '@components/LegalCategories/helpers';
import ProductStatus from '@components/ProductStatus/ProductStatus';
import { createColumnHelper } from '@tanstack/react-table';
import { parseISO } from 'date-fns';
import { getActions } from '../../../helpers';
import { AssociatedContentCell } from './components/AssociatedContentCell';
import { FileRoles } from '@common/features/document/types';

/**
 * Determines if published products can be updated based on task status.
 * - Customers can update when status is WithClientMoreInformation
 * - Admins can update when status is WithProduction, WithQC, or WithDataQuality
 */
export const canUpdatePublishedProduct = (taskStatus?: SubmissionTaskStatuses): boolean => {
  if (!taskStatus) return false;
  return [
    SubmissionTaskStatuses.WithClientMoreInformation,
    SubmissionTaskStatuses.WithProduction,
    SubmissionTaskStatuses.WithQC,
    SubmissionTaskStatuses.WithDataQuality,
  ].includes(taskStatus);
};

export interface ISubmissionNote {
  description: string;
  from: string;
  to: string;
  noteId: number;
  performedBy: string;
  text: string;
  submittedAt: string;
  fileName?: string;
  tvtFirstTvpFileName?: string;
  tvtSecondTvpFileName?: string;
  tvtPdfFileName?: string;
  files?: FileData[];
}

const ProductsTableColumnKeys = {
  productName: 'productName',
  linkedSmpc: 'linkedSmpc',
  linkedPil: 'linkedPil',
  blackTriangle: 'blackTriangle',
  legalCategories: 'legalCategories',
  maNumbers: 'maNumbers',
  atcCodes: 'atcCodes',
  allergyAndPregnancyRiskInfo: 'allergyAndPregnancyRiskInfo',
  associatedContent: 'associatedContent',
  productId: 'productId',
  status: 'status',
  details: 'details',
  actions: 'actions',
  isUpcoming: 'isUpcoming',
};

export const getProductsTableColumnData = (
  isReadonly: boolean,
  removeButtonText: string,
  showDocumentCountChanges: boolean,
  onEditXml: () => void,
  onDetailsClick: (productId: string) => void,
  onProductUpdate?: (productId: string) => void,
  onProductRemove?: (productId: string) => void,
) =>
  [
    {
      accessor: ProductsTableColumnKeys.productName,
      columnName: 'Product Name',
      cell: (info: any) => (
        <>
          {info.row.original.isUpcoming ? (
            <IconWithTooltip
              tooltipId="upcomingProduct"
              iconSrc="/icons/small_warning_icon.svg"
              tooltipText="This product will be published by another task."
            />
          ) : null}
          <CellWithChanges value={info.getValue().value} changeType={info.getValue().changeType} />
        </>
      ),
    },
    {
      accessor: ProductsTableColumnKeys.linkedSmpc,
      columnName: 'Linked SmPC',
      cell: ({ getValue }: any) => {
        return <CellWithChanges value={getValue().value} changeType={getValue().changeType} />;
      },
    },
    {
      accessor: ProductsTableColumnKeys.linkedPil,
      columnName: 'Linked PIL',
      cell: ({ getValue }: any) => {
        return <CellWithChanges value={getValue().value} changeType={getValue().changeType} />;
      },
    },
    {
      accessor: ProductsTableColumnKeys.blackTriangle,
      columnName: () => <div style={{ fontSize: '20px' }}>{'\u25BC'}</div>,
      cell: ({ getValue }: any) => <CellWithChanges value={getValue().value ? 'Yes' : 'No'} changeType={getValue().changeType} />,
    },
    {
      accessor: ProductsTableColumnKeys.legalCategories,
      columnName: 'Legal categories',
      cell: (info: any) => renderLegalCategories(info.getValue()),
    },
    {
      accessor: ProductsTableColumnKeys.maNumbers,
      columnName: 'MA Numbers',
      cell: (info: any) => {
        const maNumbers = info.getValue();
        return (
          <>
            {Object.keys(maNumbers).map((ma) => (
              <CellWithChanges key={ma} value={ma} changeType={maNumbers[ma]} />
            ))}
          </>
        );
      },
    },
    {
      accessor: ProductsTableColumnKeys.atcCodes,
      columnName: 'ATC Codes',
      cell: (info: any) => {
        const atcCodes = info.getValue();
        return (
          <>
            {Object.keys(atcCodes).map((atc) => (
              <CellWithChanges value={atc} changeType={atcCodes[atc]} />
            ))}
          </>
        );
      },
    },
    {
      accessor: ProductsTableColumnKeys.allergyAndPregnancyRiskInfo,
      columnName: 'Allergy and Pregnancy risk info',
      cell: (info: any) => {
        const { isGlutenFree, isLactoseFree, isPregnancyRisk, isSuitableForVegans } = info.getValue();
        return (
          <AllergyAndPregnancyRiskInfo
            isGlutenFree={isGlutenFree.value}
            isLactoseFree={isLactoseFree.value}
            isPregnancyRisk={isPregnancyRisk.value}
            isSuitableForVegans={isSuitableForVegans.value}
            glutenFreeChangeType={isGlutenFree.changeType}
            lactoseFreeChangeType={isLactoseFree.changeType}
            pregnancyRiskChangeType={isPregnancyRisk.changeType}
            suitableForVegansChangeType={isSuitableForVegans.changeType}
          />
        );
      },
    },
    {
      accessor: ProductsTableColumnKeys.associatedContent,
      columnName: 'Associated content',
      cell: (info: any) => (
        <AssociatedContentCell
          showChanges={showDocumentCountChanges}
          assignments={info.getValue().assignments}
          documentsData={info.getValue().documents}
        />
      ),
    },
    { accessor: ProductsTableColumnKeys.productId, columnName: 'Product ID' },
    {
      accessor: ProductsTableColumnKeys.status,
      columnName: 'Status',
      cell: (info: any) => <ProductStatus value={info.getValue()} />,
    },
    {
      accessor: ProductsTableColumnKeys.details,
      columnName: 'Details',
      cell: (info: any) => {
        const productId = info.row.original.productId;
        const handleDetailsClick = () => onDetailsClick(productId);
        return <Button onClick={handleDetailsClick} type={ButtonTypes.TEXT} text="" icon="/icons/eye.svg" height={25} />;
      },
    },
    !isReadonly
      ? {
          accessor: ProductsTableColumnKeys.actions,
          columnName: 'Actions',
          cell: (info: any) => {
            const productId = info.row.original.productId;
            const handleUpdate = onProductUpdate ? () => onProductUpdate(productId) : undefined;
            const handleRemove = onProductRemove ? () => onProductRemove(productId) : undefined;
            return getActions(info.getValue(), removeButtonText, onEditXml, () => {}, handleUpdate, handleRemove, 'products');
          },
        }
      : undefined,
  ].filter((x) => x);

const renderLegalCategories = (legalCategories: Dictionary<ChangeType>) => (
  <>
    {Object.keys(legalCategories).map((lc) => {
      const value = getLegalCategories(lc);

      return <CellWithChanges key={lc} value={value} changeType={legalCategories[lc]} />;
    })}
  </>
);

const mapUpcomingProductToData = (
  productChanges: IProductChangesDto,
  getProductDocuments: (productId: number) => IDocumentDto[],
  assignmentChanges?: IAssignmentChangesDto[],
) => {
  const allergyAndPregnancyRiskInfo = {
    isSuitableForVegans: productChanges.isSuitableForVegans,
    isLactoseFree: productChanges.isLactoseFree,
    isGlutenFree: productChanges.isGlutenFree,
    isPregnancyRisk: productChanges.isPregnancyRisk,
  };

  return {
    [ProductsTableColumnKeys.productName]: productChanges.productName,
    [ProductsTableColumnKeys.linkedSmpc]: productChanges.smpcTitle,
    [ProductsTableColumnKeys.linkedPil]: productChanges.pilTitle,
    [ProductsTableColumnKeys.blackTriangle]: productChanges.blackTriangle,
    [ProductsTableColumnKeys.legalCategories]: productChanges.legalCategories,
    [ProductsTableColumnKeys.maNumbers]: productChanges.maNumbers,
    [ProductsTableColumnKeys.atcCodes]: productChanges.atcCodes,
    [ProductsTableColumnKeys.allergyAndPregnancyRiskInfo]: allergyAndPregnancyRiskInfo,
    [ProductsTableColumnKeys.associatedContent]: {
      documents: getProductDocuments(productChanges.productId),
      assignments: (assignmentChanges ?? []).filter((o) => o.productId === productChanges.productId) ?? [],
    },
    [ProductsTableColumnKeys.productId]: productChanges.productId,
    [ProductsTableColumnKeys.status]: ProductStatusEnum.Submitted,
    [ProductsTableColumnKeys.details]: null,
    [ProductsTableColumnKeys.actions]: [],
    [ProductsTableColumnKeys.isUpcoming]: true,
  };
};

const mapProductToData = (
  product: IProductDto,
  getProductDocuments: (productId: number) => IDocumentDto[],
  hasActions: boolean,
  canUpdateProductDetails: boolean,
  canUpdateIfPublished: boolean,
  productChanges?: IProductChangesDto,
  assignmentChanges?: IAssignmentChangesDto[],
) => {
  const productName = productChanges?.productName ?? { value: product.name, changeType: ChangeType.Unchanged };
  const blackTriangle = productChanges?.blackTriangle ?? { value: product.hasBlackTriangle, changeType: ChangeType.Unchanged };
  const legalCategories = Object.fromEntries((product?.legalCategories ?? []).map((lc) => [lc, ChangeType.Unchanged]));
  const maNumbers = Object.fromEntries((product?.maNumbers ?? []).map((ma) => [ma.number, ChangeType.Unchanged]));
  const atcCodes = Object.fromEntries((product?.atcCodes ?? []).map((atc) => [atc, ChangeType.Unchanged]));
  const linkedSmpcTitle: DiffDto<string> = { value: product.linkedSmpcTitle, changeType: ChangeType.Unchanged };
  const linkedPilTitle: DiffDto<string> = { value: product.linkedPilTitle, changeType: ChangeType.Unchanged };

  const allergyAndPregnancyRiskInfo = {
    isSuitableForVegans: {
      value: productChanges ? productChanges.isSuitableForVegans.value : product.isSuitableForVegans,
      changeType: productChanges ? productChanges?.isSuitableForVegans.changeType : ChangeType.Unchanged,
    },
    isLactoseFree: {
      value: productChanges ? productChanges?.isLactoseFree.value : product.isLactoseFree,
      changeType: productChanges ? productChanges?.isLactoseFree.changeType : ChangeType.Unchanged,
    },
    isGlutenFree: {
      value: productChanges ? productChanges?.isGlutenFree.value : product.isGlutenFree,
      changeType: productChanges ? productChanges?.isGlutenFree.changeType : ChangeType.Unchanged,
    },
    isPregnancyRisk: {
      value: productChanges ? productChanges?.isPregnancyRisk.value : product.isPregnancyRisk,
      changeType: productChanges ? productChanges?.isPregnancyRisk.changeType : ChangeType.Unchanged,
    },
  };

  return {
    [ProductsTableColumnKeys.productName]: productName,
    [ProductsTableColumnKeys.linkedSmpc]: productChanges?.smpcTitle ?? linkedSmpcTitle,
    [ProductsTableColumnKeys.linkedPil]: productChanges?.pilTitle ?? linkedPilTitle,
    [ProductsTableColumnKeys.blackTriangle]: blackTriangle,
    [ProductsTableColumnKeys.legalCategories]: productChanges?.legalCategories ?? legalCategories,
    [ProductsTableColumnKeys.maNumbers]: productChanges?.maNumbers ?? maNumbers,
    [ProductsTableColumnKeys.atcCodes]: productChanges?.atcCodes ?? atcCodes,
    [ProductsTableColumnKeys.allergyAndPregnancyRiskInfo]: allergyAndPregnancyRiskInfo,
    [ProductsTableColumnKeys.associatedContent]: {
      documents: getProductDocuments(product.productId),
      assignments: (assignmentChanges ?? []).filter((o) => o.productId === product.productId) ?? [],
    },
    [ProductsTableColumnKeys.productId]: product.productId,
    [ProductsTableColumnKeys.status]: product.status,
    [ProductsTableColumnKeys.details]: null,
    [ProductsTableColumnKeys.actions]: !hasActions
      ? canUpdateIfPublished && canUpdateProductDetails
        ? [ActionType.Update]
        : []
      : [canUpdateProductDetails && ActionType.Update, ActionType.Remove],
    [ProductsTableColumnKeys.isUpcoming]: false,
  };
};

export const mapToProductsData = (
  products: IProductDto[] | undefined,
  documents: IDocumentDto[],
  submissionTaskType: SubmissionTaskType,
  productsChanges?: IProductChangesDto[],
  upcomingProductChanges?: IProductChangesDto[],
  assignmentChanges?: IAssignmentChangesDto[],
  taskStatus?: SubmissionTaskStatuses,
) => {
  const filteredDocuments = (productId: number) => documents.filter((item) => item.linkedProductIds.includes(productId));
  const canUpdateIfPublished = canUpdatePublishedProduct(taskStatus);

  const productData = products?.map((product) => {
    const isPublished = product.status === ProductStatusEnum.Published;
    const productChanges = productsChanges?.find((pc) => pc.productId === product.productId);
    const canUpdateProductDetails = canUpdateProductDetailsInTask(submissionTaskType);
    return mapProductToData(product, filteredDocuments, !isPublished, canUpdateProductDetails, isPublished && canUpdateIfPublished, productChanges, assignmentChanges);
  });

  const upcomingProductData = upcomingProductChanges?.map((productChanges) =>
    mapUpcomingProductToData(productChanges, filteredDocuments, assignmentChanges),
  );

  return [...(productData ?? []), ...(upcomingProductData ?? [])];
};

export const prepareNotesTabColumns = (showAttachmentsColumn: boolean, isTvtFeatureEnabled?: boolean, onFileDownload?: (noteId: number, fileRole?: FileRoles) => void) => {
  const columnHelper = createColumnHelper<ISubmissionNote>();

  const columns = [
    columnHelper.accessor('description', {
      header: 'Description',
      cell: ({ getValue }) => (getValue() && getValue().length > 0 ? getValue() : '-'),
    }),
    columnHelper.accessor('text', {
      header: 'Notes',
      cell: ({ getValue }) => (getValue() && getValue().length > 0 ? getValue() : '-'),
    }),
    columnHelper.accessor('from', {
      header: 'From',
      cell: ({ getValue }) => (getValue() && getValue().length > 0 ? getValue() : '-'),
    }),
    columnHelper.accessor('to', {
      header: 'To',
      cell: ({ getValue }) => (getValue() && getValue().length > 0 ? getValue() : '-'),
    }),
    columnHelper.accessor('submittedAt', {
      header: 'Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? formatDateTimeNoSeconds(parseISO(value)) : '-';
      },
    }),
    columnHelper.accessor('performedBy', {
      header: 'Performed by',
      cell: ({ getValue }) => (getValue() && getValue().length > 0 ? getValue() : '-'),
    }),
    showAttachmentsColumn &&
      columnHelper.accessor('files', {
        header: 'Attachments',
        cell: ({ getValue, row }) => {
          const val = getValue() ?? [];
          const files = isTvtFeatureEnabled ? val : getNoTvtRelatedFiles(val);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {files?.map((file, i) => (
                <StyledLink key={i} onClick={() => onFileDownload && onFileDownload(row.original.noteId, file.fileRole)}>
                  {file.originalFileName}
                </StyledLink>
              ))}
            </div>
          );
        },
      }),
  ].filter((x) => x);

  return columns;
};

const getNoTvtRelatedFiles = (files: FileData[]) => {
  const tvtFileRoles = [FileRoles.TvtFirstTvpFile, FileRoles.TvtSecondTvpFile, FileRoles.TvtPdfFile];
  return files.filter((file) => !tvtFileRoles.includes(file.fileRole!));
}
